import type { H3Fleet, SelfHostedWorker } from "./h3Fleet";

// Pure derivations for the GPU Fleet page: headline counts, what deserves an
// operator's attention, and the small formatters the tables share. No fetching,
// no React — every function takes `now` so tests are deterministic.

/** Worker states that count as "online": the box is reachable and heartbeating. */
export const ONLINE_STATES: ReadonlySet<string> = new Set([
  "idle",
  "busy",
  "draining",
  "blocked_gpu_busy",
]);

/** Warn this many days before the worker's Roles Anywhere certificate expires. */
export const CERT_WARN_DAYS = 30;
/** The host quarantines at five; flag a worker before it gets there. */
export const CONSECUTIVE_FAILURE_WARN = 3;

export type RunPodSummary = {
  idle: number;
  ready: number;
  running: number;
  booting: number;
  throttled: number;
  unhealthy: number;
  inQueue: number;
  inProgress: number;
};

export type FleetSummary = {
  /** Self-hosted workers that are reachable (any online state). */
  online: number;
  /** Idle self-hosted workers a clip can be handed to right now. */
  free: number;
  /** Self-hosted workers rendering a clip. */
  rendering: number;
  /** Self-hosted workers with a problem worth a look (see `workerAttention`). */
  attention: number;
  runpod: RunPodSummary | null;
  /** Clips waiting for any GPU: the pool queue plus RunPod's own queue. */
  waiting: number;
  poolInFlight: number | null;
  poolDlq: number | null;
};

export function certDaysLeft(
  certNotAfter: string | null,
  now: Date = new Date(),
): number | null {
  if (!certNotAfter) return null;
  const expiry = Date.parse(certNotAfter);
  if (Number.isNaN(expiry)) return null;
  return Math.floor((expiry - now.getTime()) / 86_400_000);
}

/** Why a worker deserves attention, or null when it is fine. One reason, the most urgent first. */
export function workerAttention(
  worker: SelfHostedWorker,
  now: Date = new Date(),
): string | null {
  if (worker.state === "quarantined") {
    return "quarantined after repeated failures — clear it on the box";
  }
  if (worker.state === "stale") {
    return "no heartbeat for more than 90 s";
  }
  const streak = worker.consecutive_failures ?? 0;
  if (streak >= CONSECUTIVE_FAILURE_WARN) {
    return `${streak} clips failed in a row`;
  }
  const days = certDaysLeft(worker.cert_not_after, now);
  if (days !== null && days <= CERT_WARN_DAYS) {
    return days < 0
      ? "worker certificate expired"
      : `worker certificate expires in ${days} d`;
  }
  return null;
}

export function summarizeFleet(
  fleet: H3Fleet,
  now: Date = new Date(),
): FleetSummary {
  const workers = fleet.self_hosted.workers;
  const online = workers.filter((worker) =>
    ONLINE_STATES.has(worker.state),
  ).length;
  const rendering = workers.filter((worker) => worker.state === "busy").length;
  const attention = workers.filter(
    (worker) => workerAttention(worker, now) !== null,
  ).length;
  const rp = fleet.runpod;
  const runpod: RunPodSummary | null = rp
    ? {
        idle: rp.workers.idle ?? 0,
        ready: rp.workers.ready ?? 0,
        running: rp.workers.running ?? 0,
        booting: rp.workers.initializing ?? 0,
        throttled: rp.workers.throttled ?? 0,
        unhealthy: rp.workers.unhealthy ?? 0,
        inQueue: rp.jobs.inQueue ?? 0,
        inProgress: rp.jobs.inProgress ?? 0,
      }
    : null;
  const pool = fleet.pool_queue ?? null;
  return {
    online,
    free: fleet.self_hosted.capacity,
    rendering,
    attention,
    runpod,
    waiting: (pool?.visible ?? 0) + (runpod?.inQueue ?? 0),
    poolInFlight: pool ? pool.in_flight : null,
    poolDlq: pool ? pool.dlq : null,
  };
}

export function formatAge(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${(seconds / 3600).toFixed(1)}h ago`;
}

/** How long the worker process has been up, from its `started_at`. */
export function formatUptime(
  startedAt: string | null,
  now: Date = new Date(),
): string {
  if (!startedAt) return "—";
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) return "—";
  const seconds = Math.max(0, (now.getTime() - started) / 1000);
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86_400).toFixed(1)} d`;
}

/** "just now" / "40s ago" / "3m ago" for the page header. */
export function formatUpdated(
  dataUpdatedAt: number,
  now: number = Date.now(),
): string {
  if (!dataUpdatedAt) return "—";
  const seconds = Math.max(0, (now - dataUpdatedAt) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  return `${Math.round(seconds / 60)}m ago`;
}

/** "3m 18s" / "1h 05m" for durations shown in tables. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600)
    return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
}

/** Plane ids as the composer logs them, in the words the page uses. */
export function planeLabel(plane: string | null): string {
  switch (plane) {
    case "self_hosted":
    case "aws_sqs_pool":
      return "self-hosted";
    case "runpod":
    case "runpod_serverless":
      return "RunPod";
    case null:
      return "—";
    default:
      return plane;
  }
}

/** How a job's status reads to an operator. */
export function describeJobStatus(job: {
  status: string;
  last_event: string | null;
  last_reason: string | null;
}): string {
  switch (job.status) {
    case "in_flight":
      return job.last_event === "claimed"
        ? "rendering"
        : job.last_event === "redispatched"
          ? `re-posted (${job.last_reason ?? "?"})`
          : "waiting for a claim";
    case "completed":
      return "delivered";
    case "failed":
      return `failed (${job.last_reason ?? job.last_event ?? "?"})`;
    case "unsupervised":
      return "v1 envelope — RunPod tracks it, not the supervisor";
    default:
      return "no outcome logged";
  }
}
